import { formatNanoDuration } from "@/shared/time/formatDuration";

/**
 * Format a signed nanosecond duration in a readable unit. The AI tools emit
 * durations as integer ns (`*_ns` columns, `d=<ns>ns` in text), and a reader
 * wants `923.000 ms`, not nine digits. Below 1 µs the integer ns is shown; below
 * a minute, the value is rounded half-up to three decimals in the largest of µs,
 * ms, or s that keeps the rounded value at or above 1, so `999_999_999n` reads
 * `1.000 s`, not `1000.000 ms`. From a minute up (after rounding to whole
 * milliseconds), {@link formatNanoDuration} spells out the units down to whole
 * milliseconds. The sign is kept because an offset before an anchor is
 * negative; only the magnitude reaches formatNanoDuration, which returns `""`
 * for negative input.
 */
export function formatDurationNs(ns: bigint): string {
  const sign = ns < 0n ? "-" : "";
  const magnitude = ns < 0n ? -ns : ns;
  if (magnitude < 1_000n) {
    return `${sign}${magnitude} ns`;
  }
  if (magnitude < 1_000_000n) {
    return `${sign}${thousandths(magnitude)} µs`;
  }
  const micros = divideRounded(magnitude, 1_000n);
  if (micros < 1_000_000n) {
    return `${sign}${thousandths(micros)} ms`;
  }
  const millis = divideRounded(magnitude, 1_000_000n);
  if (millis < 60_000n) {
    return `${sign}${thousandths(millis)} s`;
  }
  // formatNanoDuration converts to a double, and past its range (about 1e308)
  // the Infinity it gets formats as "".
  if (!Number.isFinite(Number(magnitude))) {
    return `${sign}${magnitude} ns`;
  }
  return `${sign}${formatNanoDuration(millis * 1_000_000n)}`;
}

/** `value / unit`, rounded half-up. */
function divideRounded(value: bigint, unit: bigint): bigint {
  return (value + unit / 2n) / unit;
}

/** A non-negative count of thousandths as a decimal with three places: `1500n` → `"1.500"`. */
function thousandths(value: bigint): string {
  return `${value / 1_000n}.${String(value % 1_000n).padStart(3, "0")}`;
}
